// §4.9 ATP Inspector (office) — "why is ATP 3,400 m?".
//
// The §3.6 terms are itemised (on-hand − reserved + in-transit + POs +
// planned WOs − WO demand) with a drill link on each term that HAS a real
// destination in this build.
//
// The whole point of this screen is the honest arithmetic: a term whose owning
// module is unbound comes back `qty_base: null, unavailable: true,
// owner_module: "10"` — it is rendered "waiting on Module 10", NEVER as 0. A
// fabricated 0 on `po_inbound` reads to a planner as "nothing is on its way"
// and they order again. When any term is unavailable the response sets
// `available_terms_only`, and the headline number is labelled a PARTIAL sum.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { CardSkeleton, EmptyState, ErrorState } from "../../../../components/states";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { ProductPicker } from "../../catalogue/components/product-picker";
import { WaitingOnModule } from "../components/inventory-ui";
import { useAtp, useFacilities, useProductIndex, type AtpTerm } from "../hooks/inventory-queries";
import { formatMeasure, formatMeasureWithUom } from "../lib/measure";

type TermKey =
  "on_hand" | "reserved" | "in_transit_inbound" | "po_inbound" | "wo_planned_output" | "wo_demand";

/** §3.6 sign convention, so the itemisation reads as arithmetic. */
const TERM_SIGN: Readonly<Record<TermKey, "+" | "−">> = {
  on_hand: "+",
  reserved: "−",
  in_transit_inbound: "+",
  po_inbound: "+",
  wo_planned_output: "+",
  wo_demand: "−",
};

/** §3.6: a term the server could not answer for is UNAVAILABLE, not zero — the
 * row names the module that owes it rather than printing a number nobody
 * measured. */
function isUnavailable(term: AtpTerm): boolean {
  return term.unavailable === true || term.qty_base === null;
}

interface TermRow {
  readonly key: TermKey;
  readonly term: AtpTerm;
}

const TERM_ORDER: readonly TermKey[] = [
  "on_hand",
  "reserved",
  "in_transit_inbound",
  "po_inbound",
  "wo_planned_output",
  "wo_demand",
];

export function AtpInspectorScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [productId, setProductId] = useState("");
  const [facilityId, setFacilityId] = useState("");
  const [date, setDate] = useState("");

  const facilities = useFacilities();
  const products = useProductIndex(productId.length > 0 ? [productId] : []);
  const product = products.byId.get(productId);

  // A single facility tenant needs no picker; pre-select the only one.
  const facilityItems = facilities.data?.items ?? [];
  const effectiveFacility =
    facilityId.length > 0 ? facilityId : facilityItems.length === 1 ? facilityItems[0]?.id : "";
  const ready = productId.length > 0 && (effectiveFacility ?? "").length > 0;

  const query = useMemo(
    () => ({
      product_id: productId,
      facility_id: effectiveFacility ?? "",
      ...(date.length > 0 ? { date } : {}),
    }),
    [productId, effectiveFacility, date],
  );
  const atp = useAtp(query, ready);

  useEffect(() => {
    if (atp.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [atp.error, navigate]);

  const uom = atp.data?.uom ?? "";
  const termColumns: DataTableColumn<TermRow>[] = [
    {
      key: "term",
      header: t("inventory.atp.colTerm"),
      id: true,
      render: (row) => (
        <>
          <span className="term-sign" aria-hidden="true">
            {TERM_SIGN[row.key]}
          </span>{" "}
          {t(`inventory.atp.term.${row.key}`)}
        </>
      ),
    },
    {
      key: "value",
      header: t("inventory.atp.colValue"),
      numeric: true,
      render: (row) =>
        isUnavailable(row.term) ? (
          <WaitingOnModule owner={row.term.owner_module} ownerCode={row.term.owner_module_code} />
        ) : (
          `${formatMeasure(row.term.qty_base, { locale: i18n.language })} ${uom}`
        ),
    },
    {
      key: "drill",
      header: t("inventory.atp.colDrill"),
      actions: true,
      render: (row) =>
        isUnavailable(row.term) ? (
          <span className="inv-muted">{t("inventory.atp.noDrill")}</span>
        ) : row.key === "on_hand" ? (
          <Link to="/inventory/movements" search={{ product_id: productId }} className="ds-lnk">
            {t("inventory.atp.drillLedger")}
          </Link>
        ) : row.key === "reserved" ? (
          // This lists the product's STOCK with each unit's hold badge — there
          // is no reservations screen in this slice, so the link says what it
          // actually opens rather than promising a list of holds.
          <Link to="/inventory/lookup" search={{ q: product?.sku ?? "" }} className="ds-lnk">
            {t("inventory.atp.drillReservedStock")}
          </Link>
        ) : row.key === "in_transit_inbound" ? (
          <Link to="/inventory/transfers" search={{ status: "dispatched" }} className="ds-lnk">
            {t("inventory.atp.drillTransfers")}
          </Link>
        ) : (
          <span className="inv-muted">{t("inventory.atp.noDrill")}</span>
        ),
    },
  ];

  return (
    <section className="atp-inspector" data-testid="atp-screen">
      <PageHeader title={t("inventory.atp.title")} />

      <div className="filter-bar">
        <div className="filter-chip filter-chip-wide">
          <span className="filter-chip-label">{t("inventory.atp.product")}</span>
          <ProductPicker
            value={productId}
            onChange={setProductId}
            placeholder={t("inventory.atp.productPlaceholder")}
            resolveLabel={(pid) => products.byId.get(pid)?.sku}
          />
        </div>
        {facilityItems.length > 1 ? (
          <label className="filter-chip">
            <span className="filter-chip-label">{t("inventory.filters.facility")}</span>
            <select
              value={facilityId}
              onChange={(event) => {
                setFacilityId(event.target.value);
              }}
            >
              <option value="">{t("inventory.atp.pickFacility")}</option>
              {facilityItems.map((facility) => (
                <option key={facility.id} value={facility.id}>
                  {facility.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="filter-chip">
          <span className="filter-chip-label">{t("inventory.atp.date")}</span>
          <input
            type="date"
            value={date}
            onChange={(event) => {
              setDate(event.target.value);
            }}
          />
        </label>
      </div>

      {!ready ? (
        <EmptyState title={t("inventory.atp.idleTitle")} body={t("inventory.atp.idleBody")} />
      ) : null}

      {ready && atp.isPending ? (
        <CardSkeleton lines={7} label={t("inventory.atp.loading")} />
      ) : null}

      {atp.error && !(atp.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("inventory.atp.errorTitle")}
          detail={atp.error.message}
          retryLabel={t("inventory.common.retry")}
          onRetry={() => void atp.refetch()}
          requestId={requestIdOf(atp.error)}
          requestIdLabel={t("inventory.common.requestId")}
        />
      ) : null}

      {atp.data !== undefined ? (
        <>
          <div
            className="atp-headline ds-card"
            data-testid="atp-headline"
            data-qty={atp.data.atp_qty}
            data-partial={String(atp.data.available_terms_only)}
          >
            <span className="atp-value" data-testid="atp-value">
              {formatMeasureWithUom(atp.data.atp_qty, atp.data.uom, { locale: i18n.language })}
            </span>
            <span className="atp-caption">
              {t("inventory.atp.asOf", {
                product: product?.sku ?? productId,
                date: atp.data.date,
              })}
            </span>
            {atp.data.available_terms_only ? (
              <p className="degraded-note is-warning" data-testid="atp-partial" role="note">
                {t("inventory.atp.partialSum")}
              </p>
            ) : null}
          </div>

          <DataTable
            columns={termColumns}
            rows={TERM_ORDER.map((key) => ({ key, term: atp.data.terms[key] }))}
            rowKey={(row) => row.key}
            empty={null}
            rowTestId="atp-term"
            rowClassName={(row) => (isUnavailable(row.term) ? "term-unavailable" : undefined)}
            rowProps={(row) => ({
              "data-term": row.key,
              "data-qty": row.term.qty_base ?? "",
              "data-unavailable": String(isUnavailable(row.term)),
              "data-owner": row.term.owner_module ?? "",
            })}
          />
        </>
      ) : null}
    </section>
  );
}
